import { BrandMark } from "@/components/brand";
import { TrackedRoomAudioRenderer } from "@/components/livekit/TrackedRoomAudioRenderer";
import { motion } from "framer-motion";
import { Room } from "livekit-client";
import { AlertTriangle, Timer, X } from "lucide-react";
import * as React from "react";
import {
  SessionHeaderContract,
  formatSessionDuration,
  sessionCopy,
} from "../../shared/lesson/view-contracts";
import VoiceAgent from "../VoiceAgent";
import { SessionMicButton } from "./SessionMicButton";

interface SessionHeaderProps extends SessionHeaderContract {
  room: Room;
  selectedLanguageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  showLanguageLevel?: boolean;
  getLanguageDisplayName: (code?: string) => string;
  formatSessionTime?: (seconds: number) => string;
  renderRoomAudio?: boolean;
  sessionControls?: React.ReactNode;
  /**
   * "external": the desktop tutor views place one avatar themselves; the header
   * renders no avatar of its own (the views reserve its circle when needed) and
   * shows `avatarNotice` instead of its own video error.
   */
  avatarPlacement?: "internal" | "external";
  avatarNotice?: string | null;
  /**
   * Standard B2C session: one quiet metadata line (language · level · time),
   * the live dot without its uppercase word, and no subtitle (DESIGN.md,
   * Session text roles).
   */
  calmMetadata?: boolean;
}

export const LEVEL_LABELS: Record<string, string> = {
  absolute_beginner: "First steps",
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

export function SessionHeader({
  room,
  isActiveSession,
  sessionDuration,
  selectedLanguageCode,
  selectedLanguageLevel,
  showLanguageLevel = true,
  isMuted,
  onToggleMute,
  onClose,
  getLanguageDisplayName,
  formatSessionTime,
  isZenMode = false,
  assistantDisplayName,
  disableAvatar = false,
  renderRoomAudio = true,
  sessionControls,
  avatarPlacement = "internal",
  avatarNotice = null,
  calmMetadata = false,
}: SessionHeaderProps) {
  const [anamAvatarConnected, setAnamAvatarConnected] = React.useState(false);
  const [anamAvatarError, setAnamAvatarError] = React.useState<string | null>(null);
  const [showAvatar, setShowAvatar] = React.useState(true);
  const internalAvatar = avatarPlacement === "internal";
  const avatarMessage = internalAvatar
    ? !disableAvatar && showAvatar
      ? anamAvatarError
      : null
    : avatarNotice;

  React.useEffect(() => {
    if (isActiveSession) return;
    setAnamAvatarConnected(false);
    setAnamAvatarError(null);
    setShowAvatar(true);
  }, [isActiveSession]);

  React.useEffect(() => {
    if (!disableAvatar) return;
    setShowAvatar(false);
  }, [disableAvatar]);

  const handleCloseAvatar = React.useCallback(async () => {
    setShowAvatar(false);
    setAnamAvatarConnected(false);
    setAnamAvatarError(null);
    try {
      await room.localParticipant.sendText("/avatar-off", { topic: "avatar-control" });
    } catch {}
  }, [room]);

  const resolvedSessionTime = formatSessionTime
    ? formatSessionTime(sessionDuration)
    : formatSessionDuration(sessionDuration);
  const showLanguageBadge = Boolean(
    selectedLanguageCode && selectedLanguageLevel && showLanguageLevel
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      data-transcript-header
      className={`z-20 border-b py-3 transition-colors duration-500 ${
        isZenMode ? "bg-white/5 border-white/5" : "border-white/10 bg-[#0f0f14]"
      }`}
    >
      {renderRoomAudio && isActiveSession && (
        <TrackedRoomAudioRenderer
          activeSession={isActiveSession}
          roomName={room.name || null}
          surface="session-header"
        />
      )}

      <div
        data-conversation-column
        className="w-full max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div data-session-toolbar className="flex items-center justify-between">
          <div data-session-identity className="flex items-center gap-3">
            {isZenMode ? (
              <div className="w-9 h-9 rounded-full bg-white/8 flex items-center justify-center border border-white/10 shrink-0">
                <div className="w-2 h-2 rounded-full bg-white/50" />
              </div>
            ) : (
              <div className="flex h-9 w-9 shrink-0 select-none items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white/[0.04]">
                <BrandMark size={36} className="h-full w-full object-contain p-0.5" />
              </div>
            )}

            <motion.div
              initial={{ x: -16, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="leading-tight"
            >
              <div
                className={`text-sm font-bold ${
                  isZenMode ? "font-serif tracking-wide text-white" : "text-white"
                }`}
              >
                {assistantDisplayName || (isZenMode ? "ZenTutor" : sessionCopy.brandAssistantName)}
              </div>
              {calmMetadata ? null : (
                <div
                  data-session-subtitle
                  className={`text-[11px] ${isZenMode ? "text-white/35 font-serif" : "text-white/40"}`}
                >
                  {isZenMode ? "Meditation coach" : "Real-time conversation"}
                </div>
              )}
            </motion.div>

            {isActiveSession && onToggleMute && (
              <div className="flex items-center">
                <SessionMicButton
                  room={room}
                  isActiveSession={isActiveSession}
                  isMuted={isMuted}
                  onToggleMute={onToggleMute}
                  isZenMode={isZenMode}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {internalAvatar && isActiveSession && !disableAvatar && showAvatar && (
              <div className="flex items-center gap-2 relative mr-6 sm:mr-8">
                <VoiceAgent
                  room={room}
                  isRecording={isActiveSession}
                  compact={true}
                  requireVideoTrack={true}
                  renderAudioRenderer={false}
                  onAvatarConnectionChange={setAnamAvatarConnected}
                  onAvatarError={setAnamAvatarError}
                />
                {anamAvatarConnected && (
                  <button
                    onClick={handleCloseAvatar}
                    className="absolute -top-1 -right-1 z-10 w-5 h-5 rounded-full bg-black/70 text-white/85 hover:text-white hover:bg-black/90 flex items-center justify-center transition-colors"
                    aria-label="Close avatar"
                    title="Close avatar"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            )}

            {onClose && (
              <motion.button
                whileTap={{ scale: 0.93 }}
                data-session-end
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-white/55 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
                aria-label="End Session"
                title="End Session (Esc)"
              >
                <X size={17} />
              </motion.button>
            )}
          </div>
        </div>

        {isActiveSession && avatarMessage && (
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-400/25 bg-amber-500/8 px-3 py-2">
            <AlertTriangle size={13} className="text-amber-300 mt-0.5 shrink-0" />
            <span className="text-xs text-amber-100/90">{avatarMessage}</span>
          </div>
        )}

        {isActiveSession && (
          <div
            data-session-metadata
            className="mt-2.5 flex min-h-7 flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] pt-2.5"
          >
            {calmMetadata ? (
              <p
                data-session-meta-line
                className="m-0 min-w-0 truncate text-[length:var(--session-meta-size)] tabular-nums text-[color:var(--session-meta-color)]"
              >
                {[
                  showLanguageBadge ? getLanguageDisplayName(selectedLanguageCode) : null,
                  showLanguageBadge
                    ? (LEVEL_LABELS[selectedLanguageLevel] ?? selectedLanguageLevel)
                    : null,
                  resolvedSessionTime,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : (
              <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-white/45">
                <div className="flex items-center gap-1.5">
                  <Timer size={12} className="text-orange-300" />
                  <span className="font-mono font-semibold tabular-nums text-white/65">
                    {resolvedSessionTime}
                  </span>
                </div>

                {showLanguageBadge && (
                  <>
                    <span aria-hidden="true" className="text-white/15">
                      /
                    </span>
                    <div className="flex max-w-full min-w-0 items-center gap-1.5">
                      <span className="truncate font-semibold text-white/60">
                        {getLanguageDisplayName(selectedLanguageCode)}
                      </span>
                      <span aria-hidden="true" className="shrink-0 text-white/20">
                        /
                      </span>
                      <span className="shrink-0 text-white/45">
                        {LEVEL_LABELS[selectedLanguageLevel] ?? selectedLanguageLevel}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex shrink-0 items-center gap-3">
              {sessionControls}
              <div data-session-live className="hidden items-center gap-1.5 sm:flex">
                <span className="relative flex h-1.5 w-1.5" aria-hidden={calmMetadata || undefined}>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-300 opacity-50" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-orange-300" />
                </span>
                <span
                  className={
                    calmMetadata ? "sr-only" : "text-[10px] font-semibold uppercase text-white/45"
                  }
                >
                  {sessionCopy.liveBadge}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
